import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import Tooltip from "../../../common/FormComponents/Tooltip";
import { MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";

type EmailDetails = {
  subject: string;
  preheaderText: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  emailType: string;
};

type EmailDetailsSectionProps = {
  emailDetails: EmailDetails;
  onChange: ({
    field,
    value,
  }: {
    field: keyof EmailDetails;
    value: string;
  }) => void;
  emailTypes: Array<{
    value: string;
    label: string;
  }>;
  errors?: Record<string, string>;
};

const EmailDetailsSection = ({
  emailDetails,
  onChange,
  emailTypes,
  errors = {},
}: EmailDetailsSectionProps) => {
  const handleChange =
    (field: keyof EmailDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSelectChange =
    (field: keyof EmailDetails) => (e: SelectChangeEvent<string>) => {
      onChange({ field, value: e.target.value });
    };

  return (
    <FormSection
      title="Email Details"
      tooltipText="Enter the basic details about your email"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div className="md:col-span-2">
          <InputField
            id="email-subject"
            name="subject"
            label="Email Subject"
            value={emailDetails.subject}
            onChange={handleChange("subject")}
            error={!!errors.subject}
            helperText={errors.subject || ""}
            required
            tooltipText="Enter a clear, descriptive subject for your email"
          />
        </div>

        <div className="md:col-span-2">
          <InputField
            id="preheader-text"
            name="preheaderText"
            label="Preheader Text"
            value={emailDetails.preheaderText}
            onChange={handleChange("preheaderText")}
            error={!!errors.preheaderText}
            helperText={errors.preheaderText || ""}
            tooltipText="This text appears in email clients as a preview"
            maxLength={100}
          />
        </div>

        <div>
          <InputField
            id="from-name"
            name="fromName"
            label="From Name"
            value={emailDetails.fromName}
            onChange={handleChange("fromName")}
            error={!!errors.fromName}
            helperText={errors.fromName || ""}
            required
            tooltipText="The name that will appear in the recipient's inbox"
          />
        </div>

        <div>
          <InputField
            id="from-email"
            name="fromEmail"
            label="From Email"
            value={emailDetails.fromEmail}
            onChange={handleChange("fromEmail")}
            error={!!errors.fromEmail}
            helperText={errors.fromEmail || ""}
            required
            tooltipText="The email address that will appear as the sender"
          />
        </div>

        <div>
          <InputField
            id="reply-to-email"
            name="replyToEmail"
            label="Reply-To Email"
            value={emailDetails.replyToEmail}
            onChange={handleChange("replyToEmail")}
            error={!!errors.replyToEmail}
            helperText={errors.replyToEmail || ""}
            tooltipText="Optional: Email address replies should be sent to (if different from From Email)"
          />
        </div>

        <div className="mb-6">
          <div className="flex items-center mb-1">
            <label
              htmlFor="email-type"
              className="block text-sm font-medium text-gray-900"
            >
              Email Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="Select the type of email" />
          </div>
          <Select
            id="email-type"
            name="emailType"
            value={emailDetails.emailType}
            onChange={handleSelectChange("emailType")}
            displayEmpty
            fullWidth
            error={!!errors.emailType}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select an email type
            </MenuItem>
            {emailTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
          {errors.emailType && (
            <p className="text-red-500 text-xs mt-1">{errors.emailType}</p>
          )}
        </div>
      </div>
    </FormSection>
  );
};

export default EmailDetailsSection;
